import { siteConfig } from "../config/siteConfig";
import { Star, Quote, ExternalLink } from "lucide-react";
import { useReviews } from "../hooks/useReviews";

const SOURCES = {
  google: {
    label: "Google",
    color: "#EA4335",
    href: (cfg) => cfg.reviewLinks?.google,
    Mark: () => (
      <svg viewBox="0 0 48 48" className="w-4 h-4" aria-hidden>
        <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/>
        <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/>
        <path fill="#FBBC05" d="M11.69 28.18c-.44-1.32-.69-2.73-.69-4.18s.25-2.86.69-4.18v-5.7H4.34A21.96 21.96 0 0 0 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z"/>
        <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7C13.42 14.62 18.27 10.75 24 10.75z"/>
      </svg>
    ),
  },
  agoda: {
    label: "Agoda",
    color: "#FF5722",
    href: (cfg) => cfg.reviewLinks?.agoda,
    Mark: () => (
      <div className="text-[10px] font-bold tracking-tight text-[#FF5722] bg-[#FF5722]/10 px-1.5 py-0.5 rounded">
        agoda
      </div>
    ),
  },
  tripadvisor: {
    label: "Tripadvisor",
    color: "#34E0A1",
    href: (cfg) => cfg.reviewLinks?.tripadvisor,
    Mark: () => (
      <div className="text-[10px] font-bold tracking-tight text-[#00aa6c] bg-[#34E0A1]/15 px-1.5 py-0.5 rounded">
        ◉ Tripadvisor
      </div>
    ),
  },
};

export default function Reviews() {
  const { reviewLinks } = siteConfig;
  const { reviews, stats: reviewStats } = useReviews();

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="section bg-[#FAF9F6]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 reveal">
          <p className="font-overline mb-4">Voices of Our Guests</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#1A362D] mb-4">
            Stories of Stay
          </h2>
          <div className="gold-divider"></div>
        </div>

        {/* Aggregated platform stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-14 reveal">
          {reviewStats?.google && (
            <a
              href={reviewLinks?.google}
              target="_blank"
              rel="noreferrer"
              data-testid="stat-google"
              className="group flex items-center justify-between gap-4 p-5 rounded-2xl bg-[#F3EFE6] hover:bg-[#1A362D] hover:text-[#FAF9F6] transition-colors"
            >
              <div className="flex items-center gap-3">
                <SOURCES.google.Mark />
                <div>
                  <p className="text-xs uppercase tracking-[0.22em] text-current/70 group-hover:text-[#C5A059]">
                    Google Reviews
                  </p>
                  <p className="font-display text-2xl text-current">
                    {reviewStats.google.rating}{" "}
                    <span className="text-base text-current/60">/ 5</span>
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-current/60">
                  {reviewStats.google.count} reviews
                </p>
                <ExternalLink className="w-4 h-4 mt-1 ml-auto opacity-60" strokeWidth={1.5} />
              </div>
            </a>
          )}
          {reviewStats?.agoda && (
            <a
              href={reviewLinks?.agoda}
              target="_blank"
              rel="noreferrer"
              data-testid="stat-agoda"
              className="group flex items-center justify-between gap-4 p-5 rounded-2xl bg-[#F3EFE6] hover:bg-[#1A362D] hover:text-[#FAF9F6] transition-colors"
            >
              <div className="flex items-center gap-3">
                <SOURCES.agoda.Mark />
                <div>
                  <p className="text-xs uppercase tracking-[0.22em] text-current/70 group-hover:text-[#C5A059]">
                    Agoda
                  </p>
                  <p className="font-display text-2xl text-current">
                    {reviewStats.agoda.rating}{" "}
                    <span className="text-base text-current/60">/ 10</span>
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-current/60">
                  {reviewStats.agoda.count} reviews
                </p>
                <ExternalLink className="w-4 h-4 mt-1 ml-auto opacity-60" strokeWidth={1.5} />
              </div>
            </a>
          )}
          {reviewStats?.tripadvisor && (
            <a
              href={reviewLinks?.tripadvisor}
              target="_blank"
              rel="noreferrer"
              data-testid="stat-tripadvisor"
              className="group flex items-center justify-between gap-4 p-5 rounded-2xl bg-[#F3EFE6] hover:bg-[#1A362D] hover:text-[#FAF9F6] transition-colors"
            >
              <div className="flex items-center gap-3">
                <SOURCES.tripadvisor.Mark />
                <div>
                  <p className="text-xs uppercase tracking-[0.22em] text-current/70 group-hover:text-[#C5A059]">
                    Tripadvisor
                  </p>
                  <p className="font-display text-2xl text-current">
                    {reviewStats.tripadvisor.rating}{" "}
                    <span className="text-base text-current/60">/ 5</span>
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-current/60">
                  {reviewStats.tripadvisor.count} reviews
                </p>
                <ExternalLink className="w-4 h-4 mt-1 ml-auto opacity-60" strokeWidth={1.5} />
              </div>
            </a>
          )}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {reviews.map((r, i) => {
            const src = SOURCES[r.source] || SOURCES.google;
            return (
              <article
                key={i}
                data-testid={`review-${i}`}
                className="reveal bg-[#F3EFE6] rounded-2xl p-7 relative flex flex-col"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <Quote
                  className="absolute top-5 right-5 w-7 h-7 text-[#C5A059]/35"
                  strokeWidth={1}
                />
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: r.rating }).map((_, j) => (
                    <Star
                      key={j}
                      className="w-4 h-4 fill-[#C5A059] text-[#C5A059]"
                    />
                  ))}
                </div>
                <p className="font-display text-lg sm:text-xl text-[#1A362D] leading-relaxed mb-6 italic flex-1">
                  "{r.text}"
                </p>
                <div className="flex items-center justify-between border-t border-[#EBE5D9] pt-4">
                  <div>
                    <p className="text-sm font-medium text-[#1A362D]">{r.name}</p>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-[#C5A059] mt-0.5">
                      {r.country}
                    </p>
                  </div>
                  <a
                    href={src.href(siteConfig)}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`View on ${src.label}`}
                    data-testid={`review-source-${i}`}
                    className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#5C4033] hover:text-[#1A362D]"
                  >
                    <src.Mark />
                  </a>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
